import React, { useState, useEffect, useRef } from 'react';
import { classNames } from 'primereact/utils';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import DialogHeader from '../../components/ui/DialogHeader';
import TicketVenta from './components/TicketVenta';

import { ProductoService } from '../../services/ProductoService';
import { CategoriaService } from '../../services/CategoriaService';
import { ClienteService } from '../../services/ClienteService';
import { VentaService } from '../../services/VentaService';
import { EmpresaService } from '../../services/EmpresaService';
import { ApiSunatService } from '../../services/ApiSunatService';
import { useAuth } from '../../context/AuthContext';

const tipoDocNaturalOptions = [
    { label: 'DNI', value: 'DNI' },
    { label: 'Carnet de Extranjería', value: 'CE' },
    { label: 'Pasaporte', value: 'PASAPORTE' },
    { label: 'PTP', value: 'PTP' },
    { label: 'CDI', value: 'CDI' }
];

const tipoClienteOptions = [
    { label: 'Persona Natural', value: 'natural' },
    { label: 'Persona Jurídica', value: 'juridica' }
];

const IGV_RATE = 0.18;

const getSerieByTipoComprobante = (tipoComprobante) => (tipoComprobante === 'Factura' ? 'F001' : 'B001');

const getTipoComprobanteSunat = (tipoComprobante) => (tipoComprobante === 'Factura' ? '01' : '03');

const mapTipoDocumentoSunat = (tipoDocumento = '') => {
    const normalized = String(tipoDocumento).toUpperCase();
    if (normalized === 'SIN DOCUMENTO') return '0';
    if (normalized === 'RUC') return '6';
    if (normalized === 'DNI') return '1';
    if (normalized === 'CE' || normalized.includes('EXTRAN')) return '4';
    if (normalized === 'PASAPORTE') return '7';
    return '0';
};

const getCorrelativoStorageKey = (tipoComprobante, empresaId, sucursalId) =>
    `pos-correlativo-${empresaId}-${sucursalId}-${tipoComprobante.toLowerCase()}`;

const getCorrelativoFromStorage = (tipoComprobante, empresaId, sucursalId) => {
    const key = getCorrelativoStorageKey(tipoComprobante, empresaId, sucursalId);
    const saved = Number(window.localStorage.getItem(key));
    return Number.isFinite(saved) && saved > 0 ? Math.trunc(saved) : 1;
};

const POS = () => {
    const { user, axiosInstance } = useAuth();
    const idEmpresa = user?.idEmpresa || 1;
    const idSucursal = user?.idSucursal || 1;

    const [ticketData, setTicketData] = useState(null);
    const [ticketDialogVisible, setTicketDialogVisible] = useState(false);
    const toast = useRef(null);

    // Data lists
    const [varianteCatalog, setVarianteCatalog] = useState([]);
    const [categorias, setCategorias] = useState([]);
    
    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategoria, setSelectedCategoria] = useState(null);
    
    // Cart state
    const [cart, setCart] = useState([]);
    const [selectedCliente, setSelectedCliente] = useState(null);
    
    // Client search
    const [clienteDocInput, setClienteDocInput] = useState('');
    const [isSearchingCliente, setIsSearchingCliente] = useState(false);
    const [clienteNotFound, setClienteNotFound] = useState(false);

    // Client creation dialog
    const [clienteDialog, setClienteDialog] = useState(false);
    const [clienteSubmitted, setClienteSubmitted] = useState(false);
    const [isSearchingDNI, setIsSearchingDNI] = useState(false);
    const emptyCliente = {
        id: null,
        tipoCliente: 'natural',
        tipoDocumento: 'DNI',
        numeroDocumento: '',
        nombres: '',
        apellidoPaterno: '',
        apellidoMaterno: '',
        razonSocial: '',
        representanteLegal: '',
        direccion: '',
        telefono: '',
        email: '',
        idEmpresa: idEmpresa
    };
    const [nuevoCliente, setNuevoCliente] = useState(emptyCliente);
    
    // Checkout Dialog
    const [checkoutDialog, setCheckoutDialog] = useState(false);
    const [checkoutData, setCheckoutData] = useState({
        tipoComprobante: 'Boleta',
        tipoDePago: 'Efectivo',
        efectivoRecibido: 0
    });
    const [isProcessing, setIsProcessing] = useState(false);
    const [empresaSunat, setEmpresaSunat] = useState(null);
    const [correlativos, setCorrelativos] = useState({
        Boleta: getCorrelativoFromStorage('Boleta', idEmpresa, idSucursal),
        Factura: getCorrelativoFromStorage('Factura', idEmpresa, idSucursal)
    });

    const tipoComprobanteOptions = [
        { label: 'Boleta', value: 'Boleta' },
        { label: 'Factura', value: 'Factura' }
    ];

    const tipoPagoOptions = [
        { label: 'Efectivo', value: 'Efectivo' },
        { label: 'Tarjeta', value: 'Tarjeta' },
        { label: 'Crédito', value: 'Crédito' },
        { label: 'Mixto', value: 'Mixto' }
    ];

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            const [prodRes, catRes, empRes] = await Promise.all([
                ProductoService.getAll(),
                CategoriaService.getAll(),
                EmpresaService.getEmpresas().catch(() => [])
            ]);
            setCategorias(catRes.data || []);
            setEmpresaSunat((empRes && empRes.length > 0) ? empRes[0] : null);

            const flatVariants = [];
            (prodRes.data || []).forEach(prod => {
                if (prod.variantes && prod.variantes.length > 0) {
                    prod.variantes.forEach(v => {
                        flatVariants.push({
                            idVariante: v.id,
                            idProducto: prod.id,
                            nombre: prod.nombre,
                            valorVariante: v.valor,
                            displayName: `${prod.nombre} - ${v.valor}`,
                            codigoBarras: v.codigoBarras || '',
                            precio: prod.precioVenta || 0,
                            imagen: prod.imagen,
                            idCategoria: prod.idCategoria,
                            stock: v.stock || prod.stock || 0,
                            stockMinimo: v.stockMinimo || prod.stockMinimo || 0
                        });
                    });
                } else {
                    flatVariants.push({
                        idVariante: null,
                        idProducto: prod.id,
                        nombre: prod.nombre,
                        valorVariante: null,
                        displayName: prod.nombre,
                        codigoBarras: '',
                        precio: prod.precioVenta || 0,
                        imagen: prod.imagen,
                        idCategoria: prod.idCategoria,
                        stock: prod.stock || 0,
                        stockMinimo: prod.stockMinimo || 0
                    });
                }
            });
            setVarianteCatalog(flatVariants);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Fallo al cargar datos del POS', life: 3000 });
        }
    };

    useEffect(() => {
        setCorrelativos({
            Boleta: getCorrelativoFromStorage('Boleta', idEmpresa, idSucursal),
            Factura: getCorrelativoFromStorage('Factura', idEmpresa, idSucursal)
        });
    }, [idEmpresa, idSucursal]);

    // ========== CLIENT SEARCH ==========
    const buscarClientePorDocumento = async () => {
        if (!clienteDocInput.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Aviso', detail: 'Ingrese un número de documento', life: 3000 });
            return;
        }
        setIsSearchingCliente(true);
        setClienteNotFound(false);
        try {
            const result = await ClienteService.getByDocumento(clienteDocInput.trim());
            if (result?.data && result.data.id) {
                setSelectedCliente(result.data);
                toast.current.show({ severity: 'success', summary: 'Cliente Encontrado', detail: result.data.nombre || result.data.razonSocial, life: 3000 });
            } else {
                setSelectedCliente(null);
                setClienteNotFound(true);
                toast.current.show({ severity: 'info', summary: 'No Encontrado', detail: 'El documento no está registrado. Puede agregar al cliente.', life: 4000 });
            }
        } catch (error) {
            setSelectedCliente(null);
            setClienteNotFound(true);
        } finally {
            setIsSearchingCliente(false);
        }
    };

    const clearCliente = () => {
        setSelectedCliente(null);
        setClienteDocInput('');
        setClienteNotFound(false);
    };

    // ========== CLIENT CREATION DIALOG ==========
    const openClienteDialog = () => {
        const isRuc = clienteDocInput.trim().length === 11;
        setNuevoCliente({
            ...emptyCliente,
            tipoCliente: isRuc ? 'juridica' : 'natural',
            tipoDocumento: isRuc ? 'RUC' : 'DNI',
            numeroDocumento: clienteDocInput.trim()
        });
        setClienteSubmitted(false);
        setClienteDialog(true);
    };

    const hideClienteDialog = () => {
        setClienteSubmitted(false);
        setClienteDialog(false);
    };

    const onClienteInputChange = (e, name) => {
        const val = (e.target && e.target.value) || '';
        let _cliente = { ...nuevoCliente };
        
        if (name === 'tipoCliente') {
            _cliente[name] = val;
            _cliente.tipoDocumento = val === 'juridica' ? 'RUC' : 'DNI';
            _cliente.numeroDocumento = ''; // Limpiar campos conflictivos
            _cliente.nombres = '';
            _cliente.apellidoPaterno = '';
            _cliente.apellidoMaterno = '';
            _cliente.razonSocial = '';
            _cliente.direccion = '';
        } else {
            _cliente[name] = val;
        }

        setNuevoCliente(_cliente);
    };

    const buscarDNI_RENIEC = async () => {
        if (!nuevoCliente.numeroDocumento || nuevoCliente.numeroDocumento.length !== 8) {
            toast.current.show({ severity: 'warn', summary: 'Aviso', detail: 'Ingrese un DNI válido de 8 dígitos', life: 3000 });
            return;
        }
        setIsSearchingDNI(true);
        try {
            const response = await axiosInstance.get(`/api/reniec/consulta/${nuevoCliente.numeroDocumento}`);
            if (response.data) {
                setNuevoCliente(prev => ({
                    ...prev,
                    nombres: response.data.nombres || '',
                    apellidoPaterno: response.data.apellidoPaterno || '',
                    apellidoMaterno: response.data.apellidoMaterno || ''
                }));
                toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Datos obtenidos de RENIEC', life: 3000 });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo consultar RENIEC', life: 3000 });
        } finally {
            setIsSearchingDNI(false);
        }
    };

    const buscarRUC_SUNAT = async () => {
        if (!nuevoCliente.numeroDocumento || nuevoCliente.numeroDocumento.length !== 11) {
            toast.current.show({ severity: 'warn', summary: 'Aviso', detail: 'Ingrese un RUC válido de 11 dígitos', life: 3000 });
            return;
        }
        setIsSearchingDNI(true); // Reutilizamos el spinner para simplicidad
        try {
            const response = await axiosInstance.get(`/api/empresas/search/ruc/${nuevoCliente.numeroDocumento}`);
            const rucData = response.data.data || response.data;
            if (rucData) {
                setNuevoCliente(prev => ({
                    ...prev,
                    razonSocial: rucData.razonSocial || rucData.nombre || '',
                    direccion: rucData.direccion || ''
                }));
                toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Datos obtenidos de SUNAT', life: 3000 });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo consultar SUNAT', life: 3000 });
        } finally {
            setIsSearchingDNI(false);
        }
    };

    const saveNuevoCliente = async () => {
        setClienteSubmitted(true);
        const isValid = nuevoCliente.numeroDocumento &&
            ((nuevoCliente.tipoCliente === 'natural' && nuevoCliente.nombres) ||
             (nuevoCliente.tipoCliente === 'juridica' && nuevoCliente.razonSocial));
        if (!isValid) return;

        try {
            const result = await ClienteService.create(nuevoCliente);
            const created = result?.data || { ...nuevoCliente, id: Date.now() };
            setSelectedCliente(created);
            setClienteDocInput(created.numeroDocumento);
            setClienteNotFound(false);
            setClienteDialog(false);
            toast.current.show({ severity: 'success', summary: 'Cliente Creado', detail: 'El cliente ha sido registrado exitosamente', life: 3000 });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el cliente', life: 3000 });
        }
    };

    // ========== CART LOGIC ==========
    const addToCart = (variante) => {
        if (variante.stock <= 0) {
           toast.current.show({ severity: 'warn', summary: 'Sin Stock', detail: `${variante.displayName} no tiene stock.`, life: 3000 });
           return;
        }
        const cartKey = variante.idVariante || `prod-${variante.idProducto}`;
        const existingItem = cart.find(item => (item.variante.idVariante || `prod-${item.variante.idProducto}`) === cartKey);
        
        if (existingItem) {
            if (existingItem.cantidad >= variante.stock) {
                toast.current.show({ severity: 'warn', summary: 'Stock Limitado', detail: `No puedes agregar más de ${variante.stock} unidades.`, life: 3000 });
                return;
            }
            const updatedCart = cart.map(item => {
                const itemKey = item.variante.idVariante || `prod-${item.variante.idProducto}`;
                return itemKey === cartKey
                    ? { ...item, cantidad: item.cantidad + 1, subtotal: (item.cantidad + 1) * item.precio }
                    : item;
            });
            setCart(updatedCart);
        } else {
            setCart([...cart, { variante, cantidad: 1, precio: variante.precio, subtotal: variante.precio }]);
        }
    };

    const getCartKey = (item) => item.variante.idVariante || `prod-${item.variante.idProducto}`;

    const updateQuantity = (cartKey, delta) => {
        const item = cart.find(i => getCartKey(i) === cartKey);
        if (!item) return;
        const newCantidad = item.cantidad + delta;
        if (newCantidad <= 0) { removeFromCart(cartKey); return; }
        if (newCantidad > item.variante.stock) {
            toast.current.show({ severity: 'warn', summary: 'Stock Limitado', detail: `Solo hay ${item.variante.stock} en inventario.`, life: 3000 });
            return;
        }
        const updatedCart = cart.map(i => 
            getCartKey(i) === cartKey ? { ...i, cantidad: newCantidad, subtotal: newCantidad * i.precio } : i
        );
        setCart(updatedCart);
    };

    const removeFromCart = (cartKey) => {
        setCart(cart.filter(item => getCartKey(item) !== cartKey));
    };

    const subtotalCart = cart.reduce((acc, item) => acc + item.subtotal, 0);
    const totalCart = subtotalCart; 
    const operacionGravadaCart = Number((totalCart / (1 + IGV_RATE)).toFixed(2));
    const igvCart = Number((totalCart - operacionGravadaCart).toFixed(2));

    const correlativoActual = correlativos[checkoutData.tipoComprobante] || 1;
    const serieActual = getSerieByTipoComprobante(checkoutData.tipoComprobante);
    const numeroComprobanteActual = `${serieActual}-${String(correlativoActual).padStart(6, '0')}`;
    
    const filteredProductos = varianteCatalog.filter(v => {
        const matchSearch = v.displayName.toLowerCase().includes(searchTerm.toLowerCase()) || (v.codigoBarras && v.codigoBarras.includes(searchTerm));
        const matchCat = selectedCategoria ? v.idCategoria === selectedCategoria : true;
        return matchSearch && matchCat;
    });

    // ========== CHECKOUT ==========
    const openCheckout = () => {
        if (cart.length === 0) {
            toast.current.show({ severity: 'warn', summary: 'Carrito Vacío', detail: 'Agrega productos antes de cobrar', life: 3000 });
            return;
        }
        if (!selectedCliente) {
            toast.current.show({ severity: 'warn', summary: 'Falta Cliente', detail: 'Busque y seleccione un cliente para la venta', life: 3000 });
            return;
        }
        setCheckoutData({ ...checkoutData, efectivoRecibido: totalCart });
        setCheckoutDialog(true);
    };

    const processCheckout = async () => {
        setIsProcessing(true);
        try {
             const serie = getSerieByTipoComprobante(checkoutData.tipoComprobante);
             const correlativo = correlativoActual;
             const codigoComprobante = `${serie}-${String(correlativo).padStart(6, '0')}`;
             const cantidadTotal = cart.reduce((acc, item) => acc + (Number(item.cantidad) || 0), 0);
             const descripcionVenta = cart.map((item) => `${item.variante.displayName} x${item.cantidad}`).join(', ').slice(0, 240);
             const nombreCliente = selectedCliente?.nombre
                || selectedCliente?.razonSocial
                || `${selectedCliente?.nombres || ''} ${selectedCliente?.apellidoPaterno || ''} ${selectedCliente?.apellidoMaterno || ''}`.trim();

             const ventaPayload = {
                 idEmpresa, idSucursal,
                 idUsuario: user?.id || 1,
                 estado: 'COMPLETADA',
                 tipoDePago: checkoutData.tipoDePago,
                 tipoComprobante: checkoutData.tipoComprobante,
                 codigoVenta: codigoComprobante,
                 pagoCon: checkoutData.efectivoRecibido,
                 vuelto: checkoutData.efectivoRecibido > totalCart ? checkoutData.efectivoRecibido - totalCart : 0,
                 efectivo: checkoutData.tipoDePago === 'Efectivo' ? totalCart : 0,
                 tarjeta: checkoutData.tipoDePago === 'Tarjeta' ? totalCart : 0,
                 credito: checkoutData.tipoDePago === 'Crédito' ? totalCart : 0,
                 montoTotal: totalCart,
                 subTotal: operacionGravadaCart,
                 totalImpuestos: igvCart,
                 idCliente: selectedCliente.id,
                 detalles: cart.map(item => ({
                     idVariante: item.variante.idVariante || item.variante.idProducto,
                     cantidad: item.cantidad,
                     precioUnitario: item.precio,
                     subTotal: item.subtotal
                 }))
             };

             const result = await VentaService.completarVentaCompleta(ventaPayload);

             try {
                const sunatPayload = {
                    empresa: {
                        ruc: empresaSunat?.ruc || user?.empresa?.ruc || '',
                        razon_social: empresaSunat?.nombre || user?.empresa?.nombre || '',
                        nombre_comercial: empresaSunat?.nombreComercial || empresaSunat?.nombre || user?.empresa?.nombre || '',
                        ubigeo: empresaSunat?.ubigeo || '',
                        departamento: empresaSunat?.departamento || '',
                        provincia: empresaSunat?.provincia || '',
                        distrito: empresaSunat?.distrito || '',
                        direccion: empresaSunat?.direccion || ''
                    },
                    cliente: {
                        tipo_documento: mapTipoDocumentoSunat(selectedCliente?.tipoDocumento),
                        nro_documento: selectedCliente?.numeroDocumento || '',
                        razon_social: nombreCliente,
                        direccion: selectedCliente?.direccion || '',
                        telefono: selectedCliente?.telefono || ''
                    },
                    comprobante: {
                        fecha_emision: new Date().toISOString().slice(0, 10),
                        tipo_comprobante: getTipoComprobanteSunat(checkoutData.tipoComprobante),
                        serie,
                        correlativo,
                        moneda: 'PEN'
                    },
                    venta: {
                        cantidad: cantidadTotal,
                        precio_total: cantidadTotal > 0 ? Number((totalCart / cantidadTotal).toFixed(2)) : 0,
                        descripcion: descripcionVenta
                    },
                    detalle_venta: {
                        op_gravado: operacionGravadaCart,
                        igv: igvCart,
                        subtotal: operacionGravadaCart,
                        total: totalCart
                    },
                    items: cart.map((item) => ({
                        nombre: item.variante.displayName,
                        precio_venta: Number(item.precio) || 0,
                        cantidad: Number(item.cantidad) || 0
                    }))
                };

                const sunatResult = await ApiSunatService.generarComprobante(sunatPayload);

                if (sunatResult?.pdf_base64) {
                    const pdfWindow = window.open('', '_blank');
                    if (pdfWindow) {
                        pdfWindow.document.write(`<iframe width="100%" height="100%" style="border:none" src="data:application/pdf;base64,${sunatResult.pdf_base64}"></iframe>`);
                    }
                }
             } catch (sunatError) {
                toast.current.show({
                    severity: 'warn',
                    summary: 'Venta registrada',
                    detail: `La venta se guardó, pero SUNAT falló: ${sunatError.message || 'sin detalle'}`,
                    life: 5000
                });
             }

             const nextCorrelativo = correlativo + 1;
             const correlativoStorageKey = getCorrelativoStorageKey(checkoutData.tipoComprobante, idEmpresa, idSucursal);
             window.localStorage.setItem(correlativoStorageKey, String(nextCorrelativo));
             setCorrelativos((prev) => ({
                ...prev,
                [checkoutData.tipoComprobante]: nextCorrelativo
             }));

             toast.current.show({ severity: 'success', summary: 'Venta Completada', detail: `Cod: ${result?.codigoVenta || result?.data?.codigoVenta || 'OK'} - ${codigoComprobante}`, life: 3500 });
             
             // Mostrar ticket dialog en vez de limpiar inmediatamente
             setTicketData({
                 venta: { ...ventaPayload, codigoVenta: codigoComprobante, fechaCreacion: new Date() },
                 detalles: cart,
                 empresa: empresaSunat,
                 cliente: selectedCliente
             });
             setCheckoutDialog(false);
             setTicketDialogVisible(true);
             loadInitialData();
             
        } catch (error) {
             const backendDetail = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Fallo al procesar la venta';
             toast.current.show({ severity: 'error', summary: 'Error', detail: backendDetail, life: 4500 });
        } finally {
            setIsProcessing(false);
        }
    };

    const clienteDialogFooter = (
        <div className="dialog-footer-buttons">
            <Button label="Cancelar" icon="pi pi-times" onClick={hideClienteDialog} className="p-button-text p-button-secondary" autoFocus />
            <Button label="Guardar Cliente" icon="pi pi-check" onClick={saveNuevoCliente} className="p-button-primary" />
        </div>
    );

    const ticketDialogFooter = (
        <div className="dialog-footer-buttons flex justify-content-between p-3 border-top-1 surface-border bg-gray-50 border-round-bottom-xl" style={{marginTop: '0px'}}>
            <Button label="Imprimir Ticket" icon="pi pi-print" onClick={() => window.print()} className="p-button-primary" autoFocus />
            <Button label="Nueva Venta" icon="pi pi-check" onClick={() => { setTicketDialogVisible(false); setCart([]); clearCliente(); loadInitialData(); }} className="p-button-text" />
        </div>
    );

    return (
        <div className="grid POS-container">
            <Toast ref={toast} />

            {/* Left side: Cart List */}
            <div className="col-12 xl:col-5">
                <div className="card shadow-2 h-full flex flex-column border-round-xl p-3">
                    <h5 className="font-bold text-xl mb-3 text-700">Punto de Venta</h5>
                    
                    {/* Customer Search */}
                    <div className="mb-4">
                        <span className="p-input-icon-left p-input-icon-right w-full">
                            <i className="pi pi-user text-primary-700" />
                            <InputText 
                                className="w-full"
                                value={clienteDocInput} 
                                onChange={(e) => { 
                                    // Validar: solo números, máximo 11 (RUC) o 8 (DNI)
                                    const val = e.target.value.replace(/[^0-9]/g, '').substring(0, 11);
                                    setClienteDocInput(val); 
                                    setClienteNotFound(false); 
                                }}
                                onKeyDown={(e) => e.key === 'Enter' && buscarClientePorDocumento()}
                                maxLength="11"
                                placeholder="Ingresar DNI / RUC del cliente"
                                disabled={!!selectedCliente}
                            />
                            {selectedCliente ? (
                                <i className="pi pi-times cursor-pointer text-red-500" onClick={clearCliente} title="Cambiar cliente" />
                            ) : (
                                <i className={classNames("pi", isSearchingCliente ? "pi-spin pi-spinner" : "pi-search", "cursor-pointer text-primary-700")} onClick={() => { if(!isSearchingCliente && clienteDocInput.trim()) buscarClientePorDocumento() }} title="Buscar cliente" />
                            )}
                        </span>

                        {/* Client found info */}
                        {selectedCliente && (
                            <div className="mt-2 p-2 bg-green-50 border-1 border-green-200 border-round flex align-items-center gap-2">
                                <i className="pi pi-check-circle text-green-600"></i>
                                <div className="flex-1">
                                    <span className="font-bold text-green-800 block">{selectedCliente.nombre || selectedCliente.razonSocial}</span>
                                    <small className="text-green-600">{selectedCliente.tipoDocumento}: {selectedCliente.numeroDocumento}</small>
                                </div>
                            </div>
                        )}

                        {/* Client not found - option to add */}
                        {clienteNotFound && !selectedCliente && (
                            <div className="mt-2 p-2 bg-orange-50 border-1 border-orange-200 border-round flex align-items-center justify-content-between">
                                <div className="flex align-items-center gap-2">
                                    <i className="pi pi-info-circle text-orange-600"></i>
                                    <span className="text-orange-800 text-sm">Cliente no encontrado</span>
                                </div>
                                <Button 
                                    label="Registrar" 
                                    icon="pi pi-user-plus" 
                                    size="small"
                                    className="p-button-outlined p-button-warning"
                                    onClick={openClienteDialog}
                                />
                            </div>
                        )}
                    </div>

                    {/* Cart Items */}
                    <div className="flex-grow-1 overflow-auto" style={{ minHeight: '300px' }}>
                        {cart.length === 0 ? (
                            <div className="flex flex-column align-items-center justify-content-center h-full text-500">
                                <i className="pi pi-shopping-cart text-6xl mb-3 opacity-30"></i>
                                <span className="font-medium text-lg opacity-60">Carrito vacío</span>
                            </div>
                        ) : (
                            <ul className="list-none p-0 m-0">
                                {cart.map((item) => {
                                    const key = getCartKey(item);
                                    return (
                                    <li key={key} className="flex align-items-center py-3 px-2 border-bottom-1 surface-border">
                                        <div className="flex-grow-1 flex flex-column">
                                            <span className="font-bold text-700">{item.variante.displayName}</span>
                                            <span className="text-500 text-sm">S/ {item.precio?.toFixed(2)} c/u</span>
                                        </div>
                                        <div className="flex align-items-center gap-2">
                                            <Button icon="pi pi-minus" className="p-button-rounded p-button-outlined p-button-secondary p-button-sm" onClick={() => updateQuantity(key, -1)} />
                                            <span className="font-bold text-lg w-2rem text-center">{item.cantidad}</span>
                                            <Button icon="pi pi-plus" className="p-button-rounded p-button-outlined p-button-secondary p-button-sm" onClick={() => updateQuantity(key, 1)} />
                                        </div>
                                        <div className="w-5rem text-right font-bold text-primary-600 text-lg">
                                            S/ {item.subtotal?.toFixed(2)}
                                        </div>
                                        <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger ml-2" onClick={() => removeFromCart(key)} />
                                    </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    {/* Summary & Checkout */}
                    <div className="mt-auto pt-4 border-top-1 surface-border">
                        <div className="grid mb-2">
                            <div className="col-6">
                                <div className="text-sm text-600">Op. Gravada</div>
                                <div className="font-bold">S/ {operacionGravadaCart.toFixed(2)}</div>
                            </div>
                            <div className="col-6 text-right">
                                <div className="text-sm text-600">IGV (18%)</div>
                                <div className="font-bold">S/ {igvCart.toFixed(2)}</div>
                            </div>
                        </div>
                        <div className="flex justify-content-between mb-2 text-xl">
                            <span className="text-600">Total a Pagar</span>
                            <span className="font-bold text-900 text-2xl">S/ {totalCart.toFixed(2)}</span>
                        </div>
                        <Button 
                            label="Cobrar" 
                            icon="pi pi-check-circle" 
                            size="large" 
                            className="w-full mt-3 p-3 text-xl font-bold border-round-lg shadow-4" 
                            disabled={cart.length === 0 || !selectedCliente}
                            onClick={openCheckout}
                        />
                    </div>
                </div>
            </div>

            {/* Right side: Catalog List */}
            <div className="col-12 xl:col-7">
                <div className="card shadow-2 h-full border-round-xl p-3 surface-ground">
                    <div className="flex flex-column md:flex-row gap-3 mb-4">
                        <span className="p-input-icon-left flex-grow-1">
                            <i className="pi pi-search" />
                            <InputText 
                                value={searchTerm} 
                                onChange={(e) => setSearchTerm(e.target.value)} 
                                placeholder="Buscar producto o escanear código..." 
                                className="w-full border-round-xl" 
                            />
                        </span>
                        <Dropdown 
                            value={selectedCategoria} 
                            options={categorias} 
                            onChange={(e) => setSelectedCategoria(e.value)} 
                            optionLabel="nombre" 
                            optionValue="id" 
                            placeholder="Todas las categorías" 
                            showClear 
                            className="w-full md:w-15rem border-round-xl" 
                        />
                    </div>

                    <div className="grid">
                        {filteredProductos.length === 0 ? (
                            <div className="col-12 text-center text-500 py-5">
                                No se encontraron productos coincidentes.
                            </div>
                        ) : (
                            filteredProductos.map((v) => {
                                const key = v.idVariante || `prod-${v.idProducto}`;
                                return (
                                <div key={key} className="col-6 sm:col-4 md:col-3 p-2">
                                    <div 
                                        className={classNames('p-3 border-round-xl shadow-2 flex flex-column align-items-center justify-content-between cursor-pointer transition-transform transition-duration-200 hover:shadow-4 h-full relative overflow-hidden', 
                                            v.stock <= 0 ? 'surface-200 opacity-60' : 'surface-card')}
                                        onClick={() => addToCart(v)}
                                    >
                                        {v.stock > 0 && v.stock <= v.stockMinimo && (
                                            <span className="absolute top-0 right-0 bg-orange-500 text-white text-xs font-bold px-2 py-1 border-round-bl-lg">
                                                Poco Stock
                                            </span>
                                        )}
                                        {v.stock <= 0 && (
                                            <span className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold px-2 py-1 border-round-bl-lg">
                                                Agotado
                                            </span>
                                        )}
                                        
                                        <div className="w-5rem h-5rem bg-primary-50 border-round-circle flex align-items-center justify-content-center mb-3 mt-2">
                                            {v.imagen ? (
                                                <img src={v.imagen} alt={v.displayName} className="w-4rem border-round-circle" />
                                            ) : (
                                                <i className="pi pi-box text-primary-400 text-4xl"></i>
                                            )}
                                        </div>
                                        
                                        <div className="text-center w-full">
                                            <div className="font-bold text-700 line-height-2 mb-1 text-overflow-ellipsis overflow-hidden white-space-nowrap" title={v.displayName}>
                                                {v.displayName}
                                            </div>
                                            <div className="text-xl font-bold text-primary-600 mb-2">
                                                S/ {v.precio?.toFixed(2)}
                                            </div>
                                            <div className="text-xs text-500 font-medium bg-surface-100 p-1 border-round">
                                                Disponibles: {v.stock}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            {/* ========== CHECKOUT DIALOG ========== */}
            <Dialog visible={checkoutDialog} style={{ width: '450px' }} header="Completar Pago" modal onHide={() => setCheckoutDialog(false)} className="p-fluid">
                <div className="text-center mb-4">
                    <div className="text-sm text-600 mb-1">Monto a Cobrar</div>
                    <div className="text-4xl font-bold text-primary-600">S/ {totalCart.toFixed(2)}</div>
                </div>

                <div className="mb-3 p-2 border-1 surface-border border-round bg-blue-50">
                    <div className="text-xs text-600">Comprobante a emitir</div>
                    <div className="font-bold text-lg text-blue-700">{numeroComprobanteActual}</div>
                </div>

                <div className="field">
                     <label>Comprobante</label>
                     <div className="flex gap-3">
                         {tipoComprobanteOptions.map((opt) => (
                             <div key={opt.value} className="flex align-items-center gap-2 flex-grow-1 p-2 border-1 surface-border border-round cursor-pointer hover:surface-hover"
                                  style={{ backgroundColor: checkoutData.tipoComprobante === opt.value ? 'var(--blue-50)' : 'transparent', borderColor: checkoutData.tipoComprobante === opt.value ? 'var(--blue-500)' : 'var(--surface-border)' }}
                                  onClick={() => setCheckoutData({...checkoutData, tipoComprobante: opt.value})}>
                                 <i className={classNames('pi', opt.value === 'Factura' ? 'pi-file' : 'pi-receipt')}></i>
                                 <span className="font-medium">{opt.label}</span>
                             </div>
                         ))}
                     </div>
                </div>

                <div className="field">
                    <label htmlFor="tipoPago">Método de Pago</label>
                    <Dropdown 
                        id="tipoPago" 
                        value={checkoutData.tipoDePago} 
                        options={tipoPagoOptions} 
                        onChange={(e) => setCheckoutData({...checkoutData, tipoDePago: e.value})} 
                    />
                </div>

                {checkoutData.tipoDePago === 'Efectivo' && (
                    <div className="field">
                        <label htmlFor="efectivo">Efectivo Recibido</label>
                        <div className="p-inputgroup">
                            <span className="p-inputgroup-addon">S/</span>
                            <InputNumber 
                                id="efectivo" 
                                value={checkoutData.efectivoRecibido} 
                                onValueChange={(e) => setCheckoutData({...checkoutData, efectivoRecibido: e.value || 0})}
                                autoFocus
                                className="font-bold text-xl"
                            />
                        </div>
                    </div>
                )}

                {checkoutData.tipoDePago === 'Efectivo' && checkoutData.efectivoRecibido > totalCart && (
                     <div className="mt-3 p-3 bg-green-50 border-1 border-green-200 border-round text-center">
                         <div className="text-sm text-green-700 font-bold mb-1">Vuelto a entregar</div>
                         <div className="text-2xl font-bold text-green-700">S/ {(checkoutData.efectivoRecibido - totalCart).toFixed(2)}</div>
                     </div>
                )}

                <div className="flex justify-content-end gap-2 mt-4">
                    <Button label="Cancelar" icon="pi pi-times" outlined onClick={() => setCheckoutDialog(false)} disabled={isProcessing} className="p-button-secondary" />
                    <Button label="Confirmar Venta" icon={isProcessing ? "pi pi-spin pi-spinner" : "pi pi-check"} onClick={processCheckout} disabled={isProcessing || (checkoutData.tipoDePago === 'Efectivo' && checkoutData.efectivoRecibido < totalCart)} />
                </div>
            </Dialog>

            {/* ========== CLIENT CREATION DIALOG ========== */}
            <Dialog 
                visible={clienteDialog} 
                style={{ width: '600px', maxWidth: '95vw' }} 
                header={
                    <DialogHeader
                        title="Registrar Cliente"
                        subtitle="Agregar un nuevo cliente al sistema"
                        icon="pi pi-user-plus"
                    />
                }
                modal 
                className="p-fluid custom-profile-dialog" 
                footer={clienteDialogFooter}
                onHide={hideClienteDialog}
            >
                <div className="formgrid grid mt-3 dialog-content-specific">
                    <div className="field col-12">
                        <label htmlFor="pos-tipoCliente">Tipo de Cliente</label>
                        <Dropdown id="pos-tipoCliente" value={nuevoCliente.tipoCliente} onChange={(e) => onClienteInputChange(e, 'tipoCliente')} options={tipoClienteOptions} />
                    </div>

                    <div className="field col-4">
                        <label htmlFor="pos-tipoDocumento">Tipo Documento</label>
                        {nuevoCliente.tipoCliente === 'juridica' ? (
                            <InputText id="pos-tipoDocumento" value="RUC" disabled />
                        ) : (
                            <Dropdown id="pos-tipoDocumento" value={nuevoCliente.tipoDocumento} onChange={(e) => onClienteInputChange(e, 'tipoDocumento')}
                                options={tipoDocNaturalOptions}
                                placeholder="Seleccione tipo"
                            />
                        )}
                    </div>
                    <div className="field col-8">
                        <label htmlFor="pos-numeroDocumento">Número Documento</label>
                        <div className="p-inputgroup">
                            {nuevoCliente.tipoCliente === 'natural' && nuevoCliente.tipoDocumento === 'DNI' && (
                                <Button icon={isSearchingDNI ? "pi pi-spin pi-spinner" : "pi pi-search"} onClick={buscarDNI_RENIEC} disabled={isSearchingDNI} tooltip="Consultar RENIEC" />
                            )}
                            {nuevoCliente.tipoCliente === 'juridica' && nuevoCliente.tipoDocumento === 'RUC' && (
                                <Button icon={isSearchingDNI ? "pi pi-spin pi-spinner" : "pi pi-search"} onClick={buscarRUC_SUNAT} disabled={isSearchingDNI} tooltip="Consultar SUNAT" />
                            )}
                            <InputText id="pos-numeroDocumento" value={nuevoCliente.numeroDocumento} onChange={(e) => onClienteInputChange(e, 'numeroDocumento')} required autoFocus className={classNames({ 'p-invalid': clienteSubmitted && !nuevoCliente.numeroDocumento })} />
                        </div>
                        {clienteSubmitted && !nuevoCliente.numeroDocumento && <small className="p-error">Documento requerido.</small>}
                    </div>

                    {nuevoCliente.tipoCliente === 'natural' && (
                        <>
                            <div className="field col-12">
                                <label htmlFor="pos-nombres">Nombres</label>
                                <InputText id="pos-nombres" placeholder="Ingresar Nombres" value={nuevoCliente.nombres} onChange={(e) => onClienteInputChange(e, 'nombres')} required className={classNames({ 'p-invalid': clienteSubmitted && !nuevoCliente.nombres })} />
                                {clienteSubmitted && !nuevoCliente.nombres && <small className="p-error">El nombre es requerido.</small>}
                            </div>
                            <div className="field col-6">
                                <label htmlFor="pos-apellidoPaterno">Apellido Paterno</label>
                                <InputText id="pos-apellidoPaterno" placeholder="Ingresar Apellido Paterno" value={nuevoCliente.apellidoPaterno} onChange={(e) => onClienteInputChange(e, 'apellidoPaterno')} />
                            </div>
                            <div className="field col-6">
                                <label htmlFor="pos-apellidoMaterno">Apellido Materno</label>
                                <InputText id="pos-apellidoMaterno" placeholder="Ingresar Apellido Materno" value={nuevoCliente.apellidoMaterno} onChange={(e) => onClienteInputChange(e, 'apellidoMaterno')} />
                            </div>
                        </>
                    )}

                    {nuevoCliente.tipoCliente === 'juridica' && (
                        <>
                            <div className="field col-12">
                                <label htmlFor="pos-razonSocial">Razón Social</label>
                                <InputText id="pos-razonSocial" placeholder="Ingresar Razón Social" value={nuevoCliente.razonSocial} onChange={(e) => onClienteInputChange(e, 'razonSocial')} required className={classNames({ 'p-invalid': clienteSubmitted && !nuevoCliente.razonSocial })} />
                                {clienteSubmitted && !nuevoCliente.razonSocial && <small className="p-error">La Razón Social es requerida.</small>}
                            </div>
                            <div className="field col-12">
                                <label htmlFor="pos-representanteLegal">Representante Legal (Opcional)</label>
                                <InputText id="pos-representanteLegal" placeholder="Ingresar Representante Legal" value={nuevoCliente.representanteLegal} onChange={(e) => onClienteInputChange(e, 'representanteLegal')} />
                            </div>
                        </>
                    )}

                    <div className="field col-12">
                        <label htmlFor="pos-direccion">Dirección</label>
                        <InputText id="pos-direccion" placeholder="Ingresar Dirección" value={nuevoCliente.direccion} onChange={(e) => onClienteInputChange(e, 'direccion')} />
                    </div>
                    <div className="field col-6">
                        <label htmlFor="pos-telefono">Teléfono</label>
                        <InputText id="pos-telefono" keyfilter="int" maxLength={9} placeholder="Ej. 999888777" value={nuevoCliente.telefono} onChange={(e) => onClienteInputChange(e, 'telefono')} />
                    </div>
                    <div className="field col-6">
                        <label htmlFor="pos-email">Email</label>
                        <InputText id="pos-email" keyfilter="email" placeholder="Ej. contacto@mail.com" value={nuevoCliente.email} onChange={(e) => onClienteInputChange(e, 'email')} />
                    </div>
                </div>
            </Dialog>

            {/* Ticket Dialog */}
            <Dialog 
                visible={ticketDialogVisible} 
                className="w-full md:w-5 lg:w-4" 
                header={<DialogHeader title="Ticket de Venta Generado" icon="pi pi-ticket" />} 
                modal 
                closable={false}
                footer={ticketDialogFooter}
                onHide={() => {}}
            >
                {ticketData && (
                    <TicketVenta 
                        venta={ticketData.venta}
                        detalles={ticketData.detalles}
                        empresa={ticketData.empresa}
                        cliente={ticketData.cliente}
                        esReimpresion={false}
                    />
                )}
            </Dialog>

        </div>
    );
};

export default POS;
